import React from "react";
import TranslationWrapper, { getTranslation } from "../../components/TranslationWrapper/TranslationWrapper";
import { withStyles } from "tss-react/mui";
import vestigingStyle from "../../assets/jss/material-dashboard-pro-react/views/vestigingStyle";
import useVestigingRelation from "../../components/Forms/useVestigingRelation";
import AuthProvider from "../../auth/AuthProvider";
import CurrencyProvider from "../../components/Forms/CurrencyProvider";
import VestigingProvider from "../../components/Forms/VestigingProvider";
import JamezzFormV2 from "../JamezzForm/JamezzFormV2";
import { formatTitle } from "../../../utils/formatting";
import { Link } from "react-router";
import parseJSONTryCatched from "../../Helpers/JSONTryCatched";
import General from "./Tabs/General";
import RawApiData from "./Tabs/RawApiData";
import ChildProducts from "./Tabs/ChildProducts";
import { connect } from "react-redux";
import ChildProductGroups from "./Tabs/ChildProductGroups";
import Translations from "./Tabs/Translations";
import ParentProducts from "./Tabs/ParentProducts";
import { useSwitchToSalesarea } from "../Vestigingen/UseSwitchToSalesarea";
import _ from "lodash";

const endPoint = "core/optionGroups";

function OptionGroupForm(props) {
  const { currency, menukaartVestigingId, vestiging, extraFields, onGetModel } = useVestigingRelation({
    vestiging_id: props.vestiging_id,
    routerMatch: props.match,
    history: props.history,
    endPoint,
  });
  useSwitchToSalesarea(vestiging);

  return (
    <AuthProvider prefix={"optionGroups"}>
      <CurrencyProvider currency={currency}>
        <VestigingProvider vestiging={vestiging}>
          <JamezzFormV2
            computedPageTitle={function (model) {
              return formatTitle(this.pageTitle, model.naam);
            }}
            customFieldSerialization={{
              child_products: (child_products) =>
                JSON.stringify(
                  _.chain(child_products)
                    .mapValues((childProduct) => _.pick(childProduct, "id"))
                    .values()
                    .value()
                ),
              parent_products: (parent_products) => JSON.stringify(parent_products),
              child_product_groups: (child_product_groups) => JSON.stringify(child_product_groups),
            }}
            endPoint={endPoint}
            excludedKeysInOutput={{ raw_api_data: true }}
            extraFields={extraFields}
            extraFormProps={{ vestiging, vestigingId: vestiging?.id ?? props.vestiging_id, menukaartVestigingId }}
            mode={"onSubmit"}
            objectsExcludedAsRelation={{
              raw_api_data: true,
              parent_products: true,
              child_products: true,
              child_product_groups: true,
              translations: true,
            }}
            onGetModel={onGetModel}
            pageTitle={getTranslation("optionGroups.form.pageTitle", "Option Group")}
            pageTitleSubject={
              <small>
                <Link to={`/manager/vestigingen/${vestiging?.id}/edit`}>{vestiging?.naam}</Link> - {vestiging?.id}
              </small>
            }
            parseInput={{
              raw_api_data: (response) => (response ? parseJSONTryCatched(response) : null),
              child_products: (response) =>
                response
                  ? response
                      .map((childProduct) => ({
                        id: childProduct.id,
                        naam: childProduct.naam,
                        prijs: childProduct.prijs,
                        sortkey: childProduct.sortkey,
                        pivot: childProduct.pivot,
                      }))
                      .sort((a, b) => (a.pivot.sort_key ?? a.sortkey) - (b.pivot.sort_key ?? b.sortkey))
                  : null,
              parent_products: (response) =>
                response
                  ? response
                      .map((parentProduct) => ({
                        id: parentProduct.id,
                        naam: parentProduct.naam,
                        prijs: parentProduct.prijs,
                        sortkey: parentProduct.sortkey,
                        pivot: parentProduct.pivot,
                      }))
                      .sort((a, b) => (a.pivot.sort_key ?? a.sortkey) - (b.pivot.sort_key ?? b.sortkey))
                  : null,
              child_product_groups: (response) =>
                response
                  ? response
                      .map((childProductGroup) => ({
                        id: childProductGroup.id,
                        naam: childProductGroup.naam,
                        sortkey: childProductGroup.sortkey,
                        pivot: childProductGroup.pivot,
                      }))
                      .sort((a, b) => (a.pivot.sort_key ?? a.sortkey) - (b.pivot.sort_key ?? b.sortkey))
                  : null,
              img_uuids: (response) => {
                return response
                  ? JSON.parse(response).map((uuid) => ({
                      uuid: uuid,
                    }))
                  : [];
              },
            }}
            parseOutput={{
              translations: (translations) => (translations ? JSON.stringify(translations) : null),
              default_counts: (default_counts) => (default_counts ? JSON.stringify(default_counts) : null),
            }}
            revalidateMode={"onChange"}
            rootRight={"optionGroups"}
            tabs={[
              {
                right: "general",
                tabButton: <TranslationWrapper tDefault={"General"} tKey={"optionGroups.form.tabs.general"} />,
                tabContent: <General />,
              },
              {
                right: "Products",
                tabButton: <TranslationWrapper tDefault={"Option Products"} tKey={"optionGroups.form.tabs.Options"} />,
                tabContent: <ChildProducts />,
              },
              {
                right: "ChildProductGroups",
                tabButton: (
                  <TranslationWrapper
                    tDefault={"ChildProductGroups"}
                    tKey={"optionGroups.form.tabs.ChildProductGroups"}
                  />
                ),
                tabContent: <ChildProductGroups />,
              },
              {
                right: "Parents",
                tabButton: (
                  <TranslationWrapper tDefault={"Linked Products"} tKey={"optionGroups.form.tabs.parentProducts"} />
                ),
                tabContent: <ParentProducts />,
              },
              {
                right: "translations",
                tabButton: (
                  <TranslationWrapper tDefault={"Translations"} tKey={"optionGroups.form.tabs.translations"} />
                ),
                tabContent: <Translations />,
              },

              {
                right: "rawapidata",
                tabButton: <TranslationWrapper tDefault={"Raw api data"} tKey={"optionGroups.form.tabs.rawapidata"} />,
                tabContent: <RawApiData />,
              },
              //
            ]}
          />
        </VestigingProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
}

const mapStateToProps = (state) => {
  return {
    vestiging_id: state.filtersV2.vestiging_id?.value,
  };
};

export default connect(mapStateToProps)(withStyles(OptionGroupForm, vestigingStyle));
